/**
 * The ledgers: Questions · Claims · Decisions, three tables in one shell.
 *
 * Together they are the group's institutional memory. The questions ledger is
 * where "someone asked about that in week two" stops being a memory and
 * becomes a row with a drafted answer and citations; the claims ledger is the
 * bargaining position, ranked; the decisions ledger is why nobody re-litigates
 * a call made three meetings ago.
 *
 * The questions ledger triages into four lanes, and the lanes are sections
 * rather than columns: with a cohort of seven the ledger holds tens of rows,
 * not hundreds, and a kanban board at that scale is mostly empty space. Every
 * lane label is text, never colour alone, so the triage survives a printed
 * export.
 *
 * The surface vocabulary is Ask's, and the change is mostly one of spelling.
 * Every rule, inset and muted colour here was written as an inline `style`
 * object — `borderColor: 'var(--color-rule)'` forty-one times — which is the
 * same border Ask draws with a class. Inline styles are why the drift was
 * invisible: two surfaces can name the same token and still disagree about
 * 12px versus 14px of padding, and nobody notices until they are opened side
 * by side. So this reaches for `.screen`, `.card`, `.card-header` and `.row`
 * rather than restating them, and the section headers are `.label` mono at the
 * 9px the spec sets rather than four sizes of sans.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Suggestions } from '../claims/Suggestions.js';
import { FilterBox, useFilter } from '../shell/Filter.js';
import {
  api,
  type LedgerClaim,
  type LedgerDecision,
  type LedgerQuestion,
} from '../lib/api.js';

const TABLES = ['Questions', 'Claims', 'Decisions'] as const;
type Table = (typeof TABLES)[number];

/**
 * The four triage lanes, plus the untriaged holding pen.
 *
 * A lane groups more than one status — "Ask the employer" holds both the
 * question that is about to be put and the one the employer has answered —
 * because the lane is about *who resolves it*, and that does not change when
 * they do.
 */
const LANES: { title: string; statuses: string[] }[] = [
  { title: 'Not yet triaged', statuses: ['open'] },
  { title: 'Answerable from the document', statuses: ['answered_from_document'] },
  {
    title: 'From the Award or NES',
    statuses: ['answered_from_award', 'answered_from_public_award'],
  },
  { title: 'Ask the employer', statuses: ['put_to_employer', 'answered_by_employer'] },
  { title: 'Needs union advice', statuses: ['needs_external_advice'] },
];

/** Where a status can be moved to, phrased as actions rather than jargon. */
const TRIAGE_OPTIONS: { status: string; label: string }[] = [
  { status: 'open', label: 'Not yet triaged' },
  { status: 'answered_from_document', label: 'Answerable from the document' },
  { status: 'answered_from_award', label: 'From the Award or NES' },
  { status: 'answered_from_public_award', label: 'From the public school Award' },
  { status: 'put_to_employer', label: 'Put to the employer' },
  { status: 'answered_by_employer', label: 'Answered by the employer' },
  { status: 'needs_external_advice', label: 'Needs union advice' },
  { status: 'superseded', label: 'Superseded — hide it' },
];

const CLAIM_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  group_endorsed: 'Endorsed',
  tabled: 'Tabled',
  employer_responded: 'Employer responded',
  agreed: 'Agreed',
  parked: 'Parked',
  withdrawn: 'Withdrawn',
};

/**
 * The one input treatment, shared by every field on this surface.
 *
 * Four different borders were in use — a hairline under one field, a strong
 * rule around another, an unstyled select — and a form whose fields do not
 * agree about their own edges reads as three forms. Ask draws its one input at
 * 6px radius on the ink rule; a ledger field is smaller and quieter, so it
 * keeps the radius and steps the rule back one weight.
 */
const FIELD =
  'hit w-full rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1.5 text-[13px] outline-none';

/** A button that does the thing. Ask's: ink ground, paper text, 6px. */
const PRIMARY = 'hit shrink-0 rounded-[6px] px-3 py-1.5 text-[12.5px] disabled:opacity-40';

export function Ledgers({
  isRep,
  onOpenClause,
}: {
  isRep: boolean;
  onOpenClause: (number: string) => void;
}) {
  const [table, setTable] = useState<Table>('Questions');

  return (
    <div className="screen">
      <div className="mb-5 flex items-center gap-1.5">
        {TABLES.map((option) => {
          const on = option === table;
          return (
            <button
              key={option}
              onClick={() => setTable(option)}
              aria-pressed={on}
              className={`hit pill text-[12.5px] ${on ? 'pill-active' : ''}`}
            >
              {option}
            </button>
          );
        })}
      </div>

      {table === 'Questions' ? <Questions isRep={isRep} onOpenClause={onOpenClause} /> : null}
      {table === 'Claims' ? <Claims isRep={isRep} onOpenClause={onOpenClause} /> : null}
      {table === 'Decisions' ? <Decisions /> : null}
    </div>
  );
}

// ─────────────────────────────── Questions

function Questions({
  isRep,
  onOpenClause,
}: {
  isRep: boolean;
  onOpenClause: (number: string) => void;
}) {
  const client = useQueryClient();
  const questions = useQuery({ queryKey: ['ledger-questions'], queryFn: api.ledgerQuestions });
  const refresh = () => client.invalidateQueries({ queryKey: ['ledger-questions'] });

  const allQuestions = questions.data?.questions ?? [];
  const {
    query,
    setQuery,
    filtered: rows,
    total,
  } = useFilter(allQuestions, (q) => [
    q.text,
    q.status,
    q.raisedBy,
    ...q.clauses.map((c) => c.number),
    ...q.clauses.map((c) => c.heading),
  ]);

  return (
    <div className="measure-wide">
      <RaiseQuestion onRaised={refresh} />

      {/*
        Below the raise form, above the lanes. Filtering empties lanes rather
        than reordering them, so a reader keeps the shape of the board — a lane
        that disappears entirely is itself information about where the matches
        are not.
      */}
      <div className="mt-4">
        <FilterBox
          label="Filter questions by text, clause, status or who raised it"
          value={query}
          onChange={setQuery}
          total={total}
          shown={rows.length}
          noun="questions"
        />
      </div>

      {LANES.map((lane) => {
        const inLane = rows.filter((q) => lane.statuses.includes(q.status));
        if (inLane.length === 0) return null;
        return (
          <section key={lane.title} className="mt-5">
            <div className="card">
              <div className="card-header">
                <p className="label m-0">{lane.title}</p>
                <span className="label">{inLane.length}</span>
              </div>
              <ul>
                {inLane.map((question) => (
                  <QuestionRow
                    key={question.id}
                    question={question}
                    isRep={isRep}
                    onOpenClause={onOpenClause}
                    onChanged={refresh}
                  />
                ))}
              </ul>
            </div>
          </section>
        );
      })}

      {rows.length === 0 && !questions.isPending ? (
        <p className="register-reading mt-6 text-[14px] text-[var(--color-muted)]">
          No questions yet. Raise one below, or select text in Read mode and choose Question.
        </p>
      ) : null}
    </div>
  );
}

function QuestionRow({
  question,
  isRep,
  onOpenClause,
  onChanged,
}: {
  question: LedgerQuestion;
  isRep: boolean;
  onOpenClause: (number: string) => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triage = useMutation({
    mutationFn: (status: string) => api.triageQuestion(question.id, status),
    onSuccess: onChanged,
  });

  return (
    <li className="border-b border-[var(--color-rule)] last:border-b-0">
      <div className="flex items-baseline gap-2.5 py-2">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="hit flex-1 text-left"
        >
          <span className="register-reading text-[14px]">{question.text}</span>
        </button>

        {question.answer ? (
          <span className="label shrink-0" title="A drafted answer is ready">
            answered
          </span>
        ) : null}
      </div>

      {open ? (
        <div className="pb-3 pl-4">
          <p className="label">
            {question.isAnonymous ? 'Raised anonymously' : `Raised by ${question.raisedBy}`}
            {' · '}
            {new Date(question.createdAt).toLocaleDateString('en-AU', {
              day: 'numeric',
              month: 'short',
            })}
          </p>

          {question.clauses.length > 0 ? (
            <p className="mt-1">
              {question.clauses.map((clause) => (
                <button
                  key={clause.number}
                  onClick={() => onOpenClause(clause.number)}
                  className="hit clause-number mr-2 text-[11px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
                  title={clause.heading ?? clause.number}
                >
                  {clause.number}
                </button>
              ))}
            </p>
          ) : null}

          {question.answer ? (
            <div className="mt-2 border-l border-[var(--color-rule-strong)] pl-3">
              <p className="register-reading text-[14px] leading-relaxed">
                {question.answer.body}
              </p>
              <p className="mt-1.5">
                {question.answer.citations.map((cite) =>
                  // A citation may name something other than a clause — the
                  // seeded answers cite claims by ref. Only clause-shaped
                  // citations link into the reader; anything else stays text
                  // rather than a button that dead-ends.
                  /^(?:[A-Z]{0,3}\d|Schedule|Part)/.test(cite.number) ? (
                    <button
                      key={cite.number}
                      onClick={() => onOpenClause(cite.number)}
                      className="hit clause-number mr-2 text-[11px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
                    >
                      {cite.number}
                    </button>
                  ) : (
                    <span key={cite.number} className="clause-number mr-2 text-[11px]">
                      {cite.number}
                    </span>
                  ),
                )}
                {/* Provenance on every AI-touched element, and a drafted
                    answer is only as good as its citations. */}
                {question.answer.draftedBy === 'ai' && !question.answer.verifiedAt ? (
                  <span className="stamp stamp-ai ml-1">AI drafted · unverified</span>
                ) : null}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-[12px] text-[var(--color-muted)]">No drafted answer yet.</p>
          )}

          {isRep ? (
            <div className="mt-2.5 flex items-baseline gap-2">
              <label className="label shrink-0" htmlFor={`triage-${question.id}`}>
                Lane
              </label>
              <select
                id={`triage-${question.id}`}
                value={question.status}
                onChange={(e) => triage.mutate(e.target.value)}
                className="hit rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1 text-[12px] outline-none"
              >
                {TRIAGE_OPTIONS.map((option) => (
                  <option key={option.status} value={option.status}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function RaiseQuestion({ onRaised }: { onRaised: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [clauses, setClauses] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const raise = useMutation({
    mutationFn: () =>
      api.raiseQuestion({
        text,
        isAnonymous: anonymous,
        clauses: clauses
          .split(/[,\s]+/)
          .map((c) => c.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setOpen(false);
      setText('');
      setClauses('');
      setAnonymous(false);
      setError(null);
      onRaised();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="hit rounded-[6px] border border-[var(--color-ink)] px-3 py-1.5 text-[12.5px]"
      >
        Raise a question
      </button>
    );
  }

  return (
    <form
      className="card"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (text.trim().length >= 5) raise.mutate();
      }}
    >
      <div className="card-header">
        <p className="label m-0">Raise a question</p>
      </div>
      <textarea
        autoFocus
        rows={2}
        aria-label="Your question about the agreement"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What does the group need answered?"
        className={FIELD}
      />
      <input
        value={clauses}
        aria-label="Clauses this question concerns"
        onChange={(e) => setClauses(e.target.value)}
        placeholder="Clauses it concerns, e.g. 19.4, 24.9 (optional)"
        className={`${FIELD} mt-2 text-[12px]`}
      />
      <label className="mt-2.5 flex items-center gap-2 text-[12.5px]">
        <input
          type="checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
        />
        Don&rsquo;t attach my name
      </label>

      {error ? (
        <p className="mt-2 text-[12.5px] leading-relaxed">{error}</p>
      ) : null}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="submit"
          disabled={raise.isPending}
          className={PRIMARY}
          style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
        >
          {raise.isPending ? 'Raising…' : 'Raise it'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="hit label underline underline-offset-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────────── Claims

function Claims({
  isRep,
  onOpenClause,
}: {
  isRep: boolean;
  onOpenClause: (number: string) => void;
}) {
  const client = useQueryClient();
  const claims = useQuery({ queryKey: ['ledger-claims'], queryFn: api.ledgerClaims });
  const refresh = () => client.invalidateQueries({ queryKey: ['ledger-claims'] });
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status?: string; priorityRank?: number | null } }) =>
      api.updateClaim(id, body),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  const all = claims.data?.claims ?? [];
  /*
   * Searchable fields named rather than the whole row stringified. A claim
   * carries an id and its owner's colour token, and matching those would mean
   * a hex code filters the list while a word matches a uuid.
   */
  const { query, setQuery, filtered: rows, total } = useFilter(all, (claim) => [
    claim.ref,
    claim.title,
    claim.category,
    claim.type,
    claim.status,
    claim.owner,
    ...claim.clauses.map((c) => c.number),
  ]);

  /**
   * Rank by swapping with the neighbour, so the list is always a permutation.
   *
   * Free-typing rank numbers invites 1, 1, 2, 7 — an ordering that quietly
   * stops meaning anything. Ranked rows sit above unranked ones; ▲ on the top
   * unranked row adopts the next rank.
   */
  const move = (claim: LedgerClaim, direction: -1 | 1) => {
    const ranked = rows.filter((r) => r.priorityRank !== null);
    const index = ranked.findIndex((r) => r.id === claim.id);

    if (index < 0) {
      if (direction === -1) {
        update.mutate({ id: claim.id, body: { priorityRank: ranked.length + 1 } });
      }
      return;
    }

    const other = ranked[index + direction];
    if (!other) return;
    update.mutate({ id: claim.id, body: { priorityRank: other.priorityRank } });
    update.mutate({ id: other.id, body: { priorityRank: claim.priorityRank } });
  };

  return (
    <div>
      {error ? (
        /*
          The fatal-objection gate speaks through here. Its refusal is the
          product working, so it is framed like Ask's refusal — hairline card,
          a mono label naming what happened, the server's sentence at reading
          size — rather than as a red line that teaches a rep to press again.
        */
        <div className="card measure mb-4">
          <p className="label mb-1">Not changed</p>
          <p className="text-[12.5px] leading-relaxed">{error}</p>
        </div>
      ) : null}

      <FilterBox
        label="Filter claims by ref, title, clause, owner or status"
        value={query}
        onChange={setQuery}
        total={total}
        shown={rows.length}
        noun="claims"
      />

      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="label border-b border-[var(--color-rule-strong)] text-left">
            <th className="w-14 py-2 pr-2 font-normal">Rank</th>
            <th className="w-12 py-2 pr-2 font-normal">Ref</th>
            <th className="py-2 pr-2 font-normal">Claim</th>
            <th className="w-24 py-2 pr-2 font-normal">Type</th>
            <th className="w-28 py-2 pr-2 font-normal">Clauses</th>
            {/* "Red team" is the schema's word, not the group's. The plain
                version has said "what the school will say" since it was
                written, and a column header that says something else about the
                same rows makes them look like two different things. */}
            <th className="w-24 py-2 pr-2 font-normal" title="What the school will say">
              Objections
            </th>
            <th className="w-36 py-2 font-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((claim) => (
            <tr key={claim.id} className="border-b border-[var(--color-rule)]">
              <td className="py-2 pr-2">
                <span className="clause-number text-[11px]">{claim.priorityRank ?? '—'}</span>
                {isRep ? (
                  <span className="ml-1">
                    <button
                      onClick={() => move(claim, -1)}
                      className="hit label"
                      aria-label={`Move ${claim.ref} up`}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => move(claim, 1)}
                      className="hit label"
                      aria-label={`Move ${claim.ref} down`}
                    >
                      ▼
                    </button>
                  </span>
                ) : null}
              </td>
              <td className="clause-number py-2 pr-2 text-[11px]">{claim.ref}</td>
              <td className="register-reading py-2 pr-2 text-[13.5px]">
                {claim.title}
                {/*
                  Demo content says so, on the row, in the ledger a
                  representative actually scans. Thirteen of these came from the
                  mockup and are owned by seven people who do not work at the
                  school; without this there is nothing on screen separating
                  them from a colleague's claim, and the ranking arrows above
                  invite reordering them as though the order meant something.
                */}
                {claim.draftedBy === 'ai_seed' ? (
                  <span className="label ml-2 text-[10px] text-[var(--color-muted)]">
                    seeded by AI
                  </span>
                ) : null}
              </td>
              <td className="py-2 pr-2">
                <span className="label">{claim.type}</span>
              </td>
              <td className="py-2 pr-2">
                {claim.clauses.map((c) => (
                  <button
                    key={`${claim.id}-${c.number}`}
                    onClick={() => onOpenClause(c.number)}
                    className="hit clause-number mr-1 text-[11px] underline decoration-[var(--color-rule-strong)] underline-offset-2"
                  >
                    {c.number}
                  </button>
                ))}
              </td>
              <td className="py-2 pr-2">
                {claim.redTeamItems > 0 ? (
                  <span className="label">{claim.redTeamItems} logged</span>
                ) : (
                  /*
                    Muted, not `--color-rule-strong`. That token is a hairline
                    colour — #b9b9b6 on paper is 1.89:1, which is legible as a
                    one-pixel border and not as nine-pixel text. The same
                    failure was found and fixed on the claim status strip.
                  */
                  <span className="label">none</span>
                )}
              </td>
              <td className="py-2">
                {isRep ? (
                  <select
                    value={claim.status}
                    onChange={(e) =>
                      update.mutate({ id: claim.id, body: { status: e.target.value } })
                    }
                    aria-label={`Status of ${claim.ref}`}
                    className="hit rounded-[5px] border border-[var(--color-rule-strong)] bg-transparent px-2 py-1 text-[12px] outline-none"
                  >
                    {Object.entries(CLAIM_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="label">{CLAIM_STATUS_LABELS[claim.status] ?? claim.status}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && !claims.isPending ? (
        <p className="register-reading mt-4 text-[14px] text-[var(--color-muted)]">
          No claims yet. Select text in Read mode and choose Claim to start one, or take one of the
          starting points below.
        </p>
      ) : null}

      {/*
        Below the ledger rather than beside it.

        These are not claims and must not read as a second list of them. The
        ledger is what the group is running; this is what the corpus thinks it
        might run, and a representative should reach it by scrolling past their
        actual work rather than by choosing between two columns.
      */}
      <section className="section-rule mt-8 pt-4">
        <h2 className="label mb-1.5">Starting points</h2>
        <Suggestions onOpenClause={onOpenClause} />
      </section>
    </div>
  );
}

// ─────────────────────────────── Decisions

function Decisions() {
  const decisions = useQuery({ queryKey: ['ledger-decisions'], queryFn: api.ledgerDecisions });
  const rows = decisions.data?.decisions ?? [];

  return (
    <div className="measure-wide">
      {rows.length > 0 ? (
        <div className="card">
          <div className="card-header">
            <p className="label m-0">Decided</p>
            <span className="label">{rows.length}</span>
          </div>
          {rows.map((decision) => (
            <DecisionRow key={decision.id} decision={decision} />
          ))}
        </div>
      ) : null}

      {rows.length === 0 && !decisions.isPending ? (
        <p className="register-reading text-[14px] text-[var(--color-muted)]">
          No decisions logged yet. Decisions are recorded from Room mode during meetings.
        </p>
      ) : null}
    </div>
  );
}

function DecisionRow({ decision }: { decision: LedgerDecision }) {
  return (
    <article className="border-b border-[var(--color-rule)] py-3 last:border-b-0">
      <div className="flex items-baseline gap-3">
        <span className="clause-number w-14 shrink-0 text-[11px] text-[var(--color-muted)]">
          {new Date(decision.occurredOn).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: '2-digit',
          })}
        </span>
        <h3 className="register-reading m-0 flex-1 text-[14px] font-semibold">
          {decision.subject}
        </h3>
        {decision.method ? <span className="label shrink-0">{decision.method}</span> : null}
      </div>

      <p className="register-reading mt-1 pl-[68px] text-[14px] leading-relaxed">
        {decision.resolution}
      </p>

      <p className="label mt-1 pl-[68px]">
        Logged by {decision.loggedBy}
        {decision.actionedBy ? ` · actioned by ${decision.actionedBy}` : ''}
      </p>

      {decision.supersedes ? (
        // The chain is the point: "we already decided that" is only useful if
        // you can show when, and what changed since.
        <p className="label mt-1 pl-[68px]">
          Supersedes “{decision.supersedesSubject}” of{' '}
          {decision.supersedesDate
            ? new Date(decision.supersedesDate).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: '2-digit',
              })
            : 'an earlier date'}
        </p>
      ) : null}
    </article>
  );
}
